import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, Loader2, RotateCw, Wallet } from "lucide-react";
import { finApi, finErr, finPref, money, LEDGER_BUCKETS, BUCKET_LABEL } from "@/lib/financeApi";
import FinLedgerDetail from "@/components/nds/windows/finance/FinLedgerDetail";
import { ArchiveViewChips, normView } from "@/components/nds/ArchiveView";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";
import { toast } from "sonner";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";


function RentAwaiting({ r, a }) {
  const v = a?.amount || 0;
  return (
    <div className="min-w-0" title={a?.invoices?.length ? `Invoices not paid yet: #${[...new Set(a.invoices)].join(", #")}` : "No unpaid employer invoice"}>
      <div className="font-mono text-[8px] tracking-[0.16em] uppercase text-nex-muted truncate">Rent · awaiting inv.</div>
      <div data-testid={`fin-ledger-rent-awaiting-${r.id}`} className={`font-mono text-[11px] truncate ${v > 0 ? "text-sky-300" : "text-nex-muted"}`}>{money(v)}</div>
    </div>
  );
}

function RentToLandlord({ r, a }) {
  const v = a?.owed || 0;
  return (
    <div className="min-w-0" title={a ? `Rent received ${money(a.received)} − already paid to landlord ${money(a.passed)}` : "No rent received yet"}>
      <div className="font-mono text-[8px] tracking-[0.16em] uppercase text-nex-muted truncate">Rent · to landlord</div>
      <div data-testid={`fin-ledger-rent-to-landlord-${r.id}`} className={`font-mono text-[11px] truncate ${v > 0 ? "text-nex-gold" : "text-nex-muted"}`}>{money(v)}</div>
    </div>
  );
}

function PaidThrough({ value, onSaved }) {
  const sa = useIsSuper();
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  if (!sa) return null;
  const save = async () => {
    try { await finApi.post("/landlord-passthrough/paid-through", { date: v, note: "Set from the Resident Ledger" }); toast.success(`Landlord paid through ${v}`); onSaved?.(); }
    catch (e) { toast.error(finErr(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 pt-1 text-[10px] text-nex-muted">
      Landlord paid through <input type="date" value={v} onChange={(e) => setV(e.target.value)} data-testid="fin-ledger-paid-through" className="bg-black/40 border border-white/10 rounded-sm px-1.5 py-0.5 text-nex-silver" />
      {v !== value && <button type="button" onClick={save} data-testid="fin-ledger-paid-through-save" className="text-nex-gold hover:underline">Save</button>}
      <span data-testid="fin-ledger-paid-through-auto">· moves forward on its own with each landlord rent payment</span>
    </div>
  );
}

function RentTotals({ awaiting, toLL, rows, onReload }) {
  const ids = new Set(rows.map((r) => r.id));
  const aw = Object.entries(awaiting).filter(([k]) => ids.has(k)).reduce((s, [, v]) => s + (v.amount || 0), 0);
  const t = toLL?.totals;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-testid="fin-ledger-rent-totals">
      <div className="p-3 rounded-md border border-sky-400/20 bg-sky-500/[0.04]" data-testid="fin-ledger-total-awaiting">
        <div className="font-mono text-[9px] tracking-[0.16em] uppercase text-nex-muted">Rent · awaiting invoice · total</div>
        <div className="font-mono text-lg text-sky-300">{money(aw)}</div>
        <div className="text-[10px] text-nex-muted">Rent on employer invoices not paid yet — not ready for the landlord</div>
      </div>
      <div className="p-3 rounded-md border border-nex-gold/30 bg-nex-gold/[0.04]" data-testid="fin-ledger-total-to-landlord">
        <div className="font-mono text-[9px] tracking-[0.16em] uppercase text-nex-muted">Rent · to landlord · total</div>
        <div className={`font-mono text-lg ${t?.owed < 0 ? "text-emerald-300" : "text-nex-gold"}`}>{t ? (t.owed < 0 ? `Paid ahead ${money(-t.owed)}` : money(t.owed)) : "—"}</div>
        {t && <div className="text-[10px] text-nex-muted">Rent received {money(t.received)} since {toLL.since} − already paid to landlord {money(t.passed)} (through {toLL.paid_through} + ticked weeks)</div>}
        {t && <div className={`text-[10px] font-mono ${Math.abs(t.check_gap) > 0.004 ? "text-amber-200" : "text-emerald-300"}`} data-testid="fin-ledger-check-gap">
          Checks written {money(t.checks)} · {Math.abs(t.check_gap) < 0.005 ? "matches the rent marked paid" : t.check_gap > 0 ? `${money(t.check_gap)} more rent marked paid than checks written` : `${money(-t.check_gap)} more in checks than rent marked paid`}</div>}
        {toLL && <PaidThrough value={toLL.paid_through} onSaved={onReload} />}
      </div>
    </div>
  );
}

function ResidentRow({ r, onOpen, awaiting, toLL }) {
  return (
    <li>
      <button type="button" onClick={() => onOpen(r)} data-testid={`fin-ledger-row-${r.id}`}
        className="w-full text-left px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 hover:bg-nex-gold/5 transition-colors">
        <div className="sm:w-44 shrink-0 min-w-0">
          <div className="text-nex-silver font-medium truncate">{r.first_name} {r.last_name}</div>
          <OpenResidentButton residentId={r.id} name={`${r.first_name} ${r.last_name}`} className="mt-1 mr-1" />
          <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full border font-mono text-[9px] tracking-[0.2em] uppercase ${
            r.status === "active" ? "border-emerald-400/40 text-emerald-300" : r.status === "archived" ? "border-amber-400/40 text-amber-300" : "border-white/15 text-nex-muted"}`}>{r.status}</span>
        </div>
        <div className="flex-1 w-full grid grid-cols-4 sm:grid-cols-9 gap-2">
          {LEDGER_BUCKETS.map((b) => (
            <div key={b} className="min-w-0">
              <div className="font-mono text-[8px] tracking-[0.16em] uppercase text-nex-muted truncate">{BUCKET_LABEL[b]}</div>
              <div data-testid={`fin-ledger-bal-${r.id}-${b}`} className={`font-mono text-[11px] truncate ${r.balances[b] < 0 ? "text-red-300" : r.balances[b] > 0 ? "text-nex-gold" : "text-nex-muted"}`}>{money(r.balances[b])}</div>
            </div>
          ))}
          <RentAwaiting r={r} a={awaiting} />
          <RentToLandlord r={r} a={toLL} />
        </div>
        <ArrowRight className="hidden sm:block h-3.5 w-3.5 text-nex-muted shrink-0" />
      </button>
    </li>
  );
}

export default function FinLedgerContent({ win }) {
  const hl = win?.payload?.highlight;
  const [view, setViewState] = useState(() => (hl ? "all" : normView(finPref.get("ledger_view", "active"))));
  const [q, setQ] = useState("");
  const [state, setState] = useState({ loading: true, residents: [], counts: {}, error: null });
  const [selected, setSelected] = useState(null);
  const [notice, setNotice] = useState(null);
  const jumped = useRef(false);
  const [awaiting, setAwaiting] = useState({});
  const [toLL, setToLL] = useState(null);

  const setView = (v) => { finPref.set("ledger_view", v); setViewState(v); };
  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    return finApi.get("/residents", { status: view })
      .then((d) => setState({ loading: false, residents: d.residents, counts: d.counts, error: null }))
      .catch((e) => setState((s) => ({ ...s, loading: false, error: finErr(e) })));
  }, [view]);
  useEffect(() => { load(); finApi.get("/rent-awaiting-invoice").then((d) => setAwaiting(d.residents)).catch(() => setAwaiting({}));
    finApi.get("/rent-to-landlord").then(setToLL).catch(() => setToLL(null)); }, [load]);
  useEffect(() => {
    if (!hl || jumped.current || state.loading) return;
    jumped.current = true;
    const r = hl.resident_id && state.residents.find((x) => x.id === hl.resident_id);
    if (r) setSelected(r); else setNotice("That entry is no longer in view");
  }, [hl, state.loading, state.residents]);

  if (selected) return <FinLedgerDetail resident={selected} highlight={hl?.resident_id === selected.id ? hl : null} onBack={() => { setSelected(null); load(); }} />;

  const list = state.residents.filter((r) => `${r.first_name} ${r.last_name}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="fin-ledger-content" className="flex flex-col h-full">
      <div className="px-5 py-3 border-b border-white/10 flex flex-wrap items-center gap-2">
        <Wallet className="h-4 w-4 text-nex-gold" />
        <input data-testid="fin-ledger-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search residents…"
          className="flex-1 min-w-[160px] px-3 py-1.5 bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none text-nex-silver text-sm rounded-sm" />
        <ArchiveViewChips prefix="fin-ledger" view={view} setView={setView} counts={state.counts} />
        <button type="button" onClick={load} data-testid="fin-ledger-refresh" aria-label="Refresh" className="p-1.5 text-nex-muted hover:text-nex-gold"><RotateCw className="h-3.5 w-3.5" /></button>
      </div>
      {notice && <div data-testid="fin-ledger-notice" className="px-5 py-2 text-xs text-amber-200 bg-amber-500/10 border-b border-amber-400/30">{notice}</div>}
      {!state.loading && !state.error && <div className="px-5 pt-3"><RentTotals awaiting={awaiting} toLL={toLL} rows={list} onReload={() => finApi.get("/rent-to-landlord").then(setToLL)} /></div>}
      {state.loading ? (
        <div data-testid="fin-ledger-loading" className="flex-1 flex items-center justify-center gap-2 p-10 text-nex-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading ledger…</div>
      ) : state.error ? (
        <div data-testid="fin-ledger-error" className="p-10 text-center space-y-3">
          <p className="text-red-300 text-sm">{state.error}</p>
          <button type="button" onClick={load} data-testid="fin-ledger-retry" className="px-4 py-2 border border-nex-gold/50 text-nex-gold font-mono text-[10px] tracking-[0.2em] uppercase rounded-sm">Retry</button>
        </div>
      ) : (
        <ul data-testid="fin-ledger-list" className="flex-1 overflow-y-auto divide-y divide-white/5">
          {list.map((r) => <ResidentRow key={r.id} r={r} onOpen={setSelected} awaiting={awaiting[r.id]} toLL={toLL?.residents?.[r.id]} />)}
          {!list.length && <li data-testid="fin-ledger-empty" className="px-5 py-10 text-center text-sm text-nex-muted">No residents match.</li>}
        </ul>
      )}
    </div>
  );
}
